/**
 * ConditionQuestionnaire - Grouped checkboxes for workflow conditions
 * Stitch design system
 */
import React from 'react';
import { Box, Typography, Checkbox, Tooltip } from '@mui/material';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const DEFAULT_CONDITIONS = [
  { code: 'location_needed', name: 'Locatie nodig?', icon: '📍', description: 'Extern draaien op locatie (niet in studio)', category: 'production' },
  { code: 'cast_needed', name: 'Cast nodig?', icon: '👥', description: 'Acteurs, modellen, of presentatoren inhuren', category: 'production' },
  { code: 'props_needed', name: 'Props nodig?', icon: '📦', description: 'Props, rekwisieten, of styling elementen', category: 'production' },
  { code: 'outdoor_shoot', name: 'Buitenopname?', icon: '☀️', description: 'Buiten draaien (weerafhankelijk)', category: 'production' },
  { code: 'script_needed', name: 'Script nodig?', icon: '📝', description: 'Geschreven script of draaiboek vereist', category: 'creative' },
  { code: 'animation_needed', name: 'Animatie nodig?', icon: '✨', description: 'Motion graphics, 2D of 3D animatie', category: 'creative' },
  { code: 'voice_over_needed', name: 'Voice-over nodig?', icon: '🎙️', description: 'Voice-over opname vereist', category: 'creative' },
  { code: 'external_crew', name: 'Externe crew?', icon: '👤', description: 'Freelancers of extern team inhuren', category: 'logistics' },
  { code: 'multiple_locations', name: 'Meerdere locaties?', icon: '🗺️', description: 'Meer dan 1 draailocatie', category: 'logistics' },
];

const CATEGORIES = {
  production: { label: 'Productie', icon: '🎥', color: '#3b82f6' },
  creative: { label: 'Creatief', icon: '✏️', color: '#8b5cf6' },
  logistics: { label: 'Logistiek', icon: '📦', color: '#f59e0b' },
};

function ConditionQuestionnaire({
  conditions: conditionsList = DEFAULT_CONDITIONS,
  selectedConditions = {},
  onConditionsChange,
  taskCountByCondition = {},
}) {
  const handleToggle = (code) => {
    onConditionsChange({ ...selectedConditions, [code]: !selectedConditions[code] });
  };

  const conditionsToShow = conditionsList.length > 0 ? conditionsList : DEFAULT_CONDITIONS;
  const groupedConditions = conditionsToShow.reduce((acc, cond) => {
    const cat = cond.category || 'production';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(cond);
    return acc;
  }, {});
  const activeCount = Object.values(selectedConditions).filter(Boolean).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Welke elementen zijn nodig?
        </Typography>
        <Typography component="span" sx={{
          fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
          color: activeCount > 0 ? T.primary : T.outlineVariant,
          bgcolor: activeCount > 0 ? T.primaryContainer : T.surfaceLow,
          px: 1.25, py: 0.3, borderRadius: '9999px',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          {activeCount} geselecteerd
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Object.entries(CATEGORIES).map(([catKey, catMeta]) => {
          const catConditions = groupedConditions[catKey] || [];
          if (catConditions.length === 0) return null;

          return (
            <Box key={catKey} sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2, boxShadow: T.shadow }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: '1rem' }}>{catMeta.icon}</Typography>
                <Typography sx={{ fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>
                  {catMeta.label}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {catConditions.map((cond) => {
                  const isChecked = selectedConditions[cond.code] || false;
                  const taskCount = taskCountByCondition[cond.code];

                  return (
                    <Tooltip key={cond.code} title={cond.description} placement="top" arrow
                      slotProps={{
                        tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } },
                        arrow: { sx: { color: T.text, '&::before': { border: 'none' } } },
                      }}
                    >
                      <Box
                        onClick={() => handleToggle(cond.code)}
                        sx={{
                          px: 1.5, py: 0.75, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 0.75,
                          borderRadius: '0.5rem',
                          border: `1.5px solid ${isChecked ? catMeta.color : 'transparent'}`,
                          bgcolor: isChecked ? `${catMeta.color}10` : T.surfaceLow,
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: catMeta.color, bgcolor: `${catMeta.color}08` },
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          size="small"
                          sx={{ p: 0, color: catMeta.color, '&.Mui-checked': { color: catMeta.color } }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggle(cond.code)}
                        />
                        <Typography sx={{ fontSize: '0.88rem' }}>{cond.icon}</Typography>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: isChecked ? 700 : 500, color: T.text }}>
                          {cond.name.replace('?', '')}
                        </Typography>
                        {taskCount && (
                          <Typography component="span" sx={{
                            fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700,
                            color: T.primary, bgcolor: T.primaryContainer,
                            px: 0.75, py: 0.15, borderRadius: '9999px', ml: 0.25,
                          }}>
                            +{taskCount}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ConditionQuestionnaire;
